<template>
  <section class="forgot-password-page">
    <div class="forgot-password-card">

      <!-- Reset Password -->
      <template v-if="isToken">
        <div class="forgot-password-illustration">
          <img src="@/assets/images/set-password.svg" alt="Forgot password" />
        </div>
        <h1>Reset your password</h1>

        <p class="description">
          Create a new password to regain access to your DTX Manufacturing
          account.
        </p>

        <div class="form">
          <div class="input-group">
            <label for="new-password"> New password <span>*</span> </label>

            <div class="input-wrapper">
              <i class="fa fa-lock"></i>

              <input
                id="new-password"
                v-model="newPassword"
                :type="passwordType"
                placeholder="Enter new password"
              />

              <img
                v-if="passwordType === 'password'"
                src="@/assets/images/icons/open-eye.svg"
                alt="Show password"
                @click="passwordType = 'text'"
              />

              <img
                v-else
                src="@/assets/images/icons/hide-eye.svg"
                alt="Hide password"
                @click="passwordType = 'password'"
              />
            </div>
          </div>

          <div class="password-requirements">
            <div class="requirements-title">Password must contain:</div>

            <div class="requirements-grid">
              <div class="requirement" :class="{ valid: hasMinLength }">
                <span class="requirement-icon">✓</span>
                <span>At least 8 characters</span>
              </div>

              <div class="requirement" :class="{ valid: hasNumber }">
                <span class="requirement-icon">✓</span>
                <span>One number</span>
              </div>

              <div class="requirement" :class="{ valid: hasUppercase }">
                <span class="requirement-icon">✓</span>
                <span>One uppercase letter</span>
              </div>

              <div class="requirement" :class="{ valid: hasSpecialCharacter }">
                <span class="requirement-icon">✓</span>
                <span>One special character</span>
              </div>

              <div class="requirement" :class="{ valid: hasLowercase }">
                <span class="requirement-icon">✓</span>
                <span>One lowercase letter</span>
              </div>
            </div>
          </div>

          <div class="input-group">
            <label for="confirm-password">
              Confirm new password <span>*</span>
            </label>

            <div class="input-wrapper">
              <i class="fa fa-lock"></i>

              <input
                id="confirm-password"
                v-model="confirmPassword"
                :type="confirmPasswordType"
                placeholder="Confirm new password"
              />

              <img
                v-if="confirmPasswordType === 'password'"
                src="@/assets/images/icons/open-eye.svg"
                alt="Show password"
                @click="confirmPasswordType = 'text'"
              />

              <img
                v-else
                src="@/assets/images/icons/hide-eye.svg"
                alt="Hide password"
                @click="confirmPasswordType = 'password'"
              />
            </div>
            <span
              v-if="confirmPassword && !passwordsMatch"
              class="password-error"
            >
              Passwords do not match.
            </span>
          </div>

          <button type="button" class="submit-btn" :disabled="isSubmitDisabled" @click="tryResetPassword">
            Reset Password
          </button>
        </div>

       <div class="divider">
          <span></span>
          <label>OR</label>
          <span></span>
        </div>

        <button
          type="button"
          class="back-login"
          @click="$router.push('/login')"
        >
          <span class="arrow">←</span>
          Back to login
        </button>
      </template>

      <!-- Forgot Password -->
      <template v-else>
        <div class="forgot-password-illustration">
          <img
            src="@/assets/images/forgot-password.svg"
            alt="Forgot password"
          />
        </div>
        <h1>Forgot Password?</h1>

        <p class="description">
          No worries! Enter your email address and we'll<br />
          send you a link to reset your password.
        </p>

        <div class="form">
          <div class="input-group">
            <label for="email"> Email address <span>*</span> </label>

            <div class="input-wrapper">
              <i class="fa fa-envelope-o"></i>

              <input
                id="email"
                v-model="email"
                type="email"
                placeholder="Enter your email"
              />
            </div>
          </div>

          <button type="button" class="submit-btn" @click="sendEmail">
            Send Reset Link
          </button>
        </div>

        <div class="divider">
          <span></span>
          <label>OR</label>
          <span></span>
        </div>

        <button
          type="button"
          class="back-login"
          @click="$router.push('/login')"
        >
          <span class="arrow">←</span>
          Back to login
        </button>
      </template>
    </div>
  </section>
</template>

<script>
import Config from '../../config'
import Loader from '@/plugins/loader'
import signupValidation from '@/helper/formValidation/signupValidation.js'
import { forgotPassword, resetPassword } from '../../api/apis/forgotPassword'
export default {
  name: 'Forgot Password Page',
  data () {
    return {
      email: '',
      isToken: false,
      newPassword: '',
      confirmPassword: '',
      passwordType: 'password',
      confirmPasswordType: 'password'
    }
  },
  computed: {
    hasMinLength () {
      return this.newPassword.length >= 8
    },
    hasNumber () {
      return /\d/.test(this.newPassword)
    },
    hasUppercase () {
      return /[A-Z]/.test(this.newPassword)
    },
    hasLowercase () {
      return /[a-z]/.test(this.newPassword)
    },
    hasSpecialCharacter () {
      return /[^A-Za-z0-9]/.test(this.newPassword)
    },
    isPasswordValid () {
      return (
        this.hasMinLength &&
        this.hasNumber &&
        this.hasUppercase &&
        this.hasLowercase &&
        this.hasSpecialCharacter
      )
    },
    passwordsMatch () {
      return this.newPassword === this.confirmPassword
    },
    isSubmitDisabled () {
      return (
        !this.isPasswordValid ||
        !this.confirmPassword ||
        !this.passwordsMatch
      )
    }
  },
  methods: {
    sendEmail () {
      const loader = new Loader()
      loader.show()
      forgotPassword(this.email).then((res) => {
        loader.hide()
        if (res.message === 'Reset Password Email Sent') {
          this.$notify.success(res.message)
        } else {
          this.$notify.alert(res.message)
        }
      }).catch(() => {
        this.$notify.alert('Something went wrong')
      })
    },
    tryResetPassword () {
      const loader = new Loader()
      loader.show()
      const body = { password: this.newPassword, confirmPassword: this.confirmPassword }
      if (signupValidation(body)) {
        resetPassword(this.newPassword).then((res) => {
          if (res.message === 'Password Updated Successfully') {
            localStorage.removeItem(Config.localstorageKeys.AUTH)
            this.$router.push('/login')
          } else {
            this.$notify.alert(res.message)
          }
        }).catch(() => {
          this.$notify.alert('Something went wrong')
        }).finally(() => {
          loader.hide()
        })
      } else {
        loader.hide()
      }
    }
  },
  mounted () {
    if (this.$route.query?.token) {
      localStorage.setItem(Config.localstorageKeys.AUTH, this.$route.query?.token)
      this.isToken = true
    }
  }
}
</script>

<style lang="scss" scoped>
.forgot-password-page {
  min-height: 100vh;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 30px 20px;
  background: #faf8f4;
  font-family: "Nunito Sans", sans-serif;
}

.forgot-password-card {
  width: 100%;
  max-width: 490px;
  padding: 18px 42px 24px;
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.07);
  text-align: center;
}

.forgot-password-illustration {
  width: 100%;
  height: 170px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.forgot-password-illustration img {
  width: 245px;
  height: 170px;
  object-fit: contain;
}

.forgot-password-card h1 {
  margin: 0;
  color: #34383d;
  font-size: 27px;
  line-height: 36px;
  font-weight: 600;
}

.description {
  margin: 8px 0 26px;
  color: #6f7378;
  font-size: 14px;
  line-height: 22px;
}

.form {
  width: 100%;
  text-align: left;
}

.input-group {
  width: 100%;
  margin-bottom: 18px;
}

.input-group label {
  display: block;
  margin-bottom: 7px;
  color: #45484c;
  font-size: 13px;
  font-weight: 500;
}

.input-group label span {
  color: #c7983a;
}

.input-wrapper {
  position: relative;
  width: 100%;
}

.input-wrapper i {
  position: absolute;
  top: 50%;
  left: 13px;
  transform: translateY(-50%);
  color: #8b8f93;
  font-size: 14px;
}

.input-wrapper input {
  width: 100%;
  height: 37px;
  box-sizing: border-box;
  padding: 0 12px 0 39px;
  border: 1px solid #dedede;
  border-radius: 4px;
  outline: none;
  color: #444;
  background: #fff;
  font-size: 13px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.input-wrapper input::placeholder {
  color: #9b9da0;
}

.input-wrapper input:focus {
  border-color: #c79a3c;

  box-shadow: 0 0 0 2px rgba(199, 154, 60, 0.08);
}

.input-wrapper > img {
  position: absolute;
  top: 50%;
  right: 12px;
  width: 14px;
  height: 14px;
  padding: 0;
  transform: translateY(-50%);
  cursor: pointer;
  opacity: 0.7;
}

.submit-btn {
  width: 100%;
  height: 35px;
  margin-top: 1px;
  border: 0;
  border-radius: 4px;
  background: #c59638;
  color: #ffffff;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.1s ease;
}

.submit-btn:hover {
  background: #b78931;
}

.submit-btn:active {
  transform: translateY(1px);
}

.submit-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.password-requirements {
  margin: 5px 0 18px;
}

.requirements-title {
  margin-bottom: 7px;
  color: #555b60;
  font-size: 12px;
  font-weight: 500;
}

.requirements-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 18px;
  row-gap: 6px;
}

.requirement {
  display: flex;
  align-items: center;
  color: #d9534f;
  font-size: 12px;
  line-height: 16px;
}

.requirement-icon {
  width: 12px;
  height: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-right: 6px;
  box-sizing: border-box;
  border: 1px solid #d9534f;
  border-radius: 50%;
  color: #d9534f;
  font-size: 8px;
  font-weight: 600;
  transition: all 0.2s ease;
}

.requirement.valid {
  color: #555b60;
}

.requirement.valid .requirement-icon {
  background: #c59638;
  border-color: #c59638;
  color: #fff;
}

.password-error {
  display: block;
  margin-top: 6px;
  color: #d9534f;
  font-size: 12px;
  line-height: 16px;
}

.divider {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50%;
  margin: 20px auto 14px;
}

.divider span {
  flex: 1;
  height: 1px;
  background: #f1efed;
}

.divider label {
  margin: 0 15px;
  color: #858585;
  font-size: 12px;
  font-weight: 500;
}

.back-login {
  border: 0;
  background: transparent;
  color: #c18f2e;
  font-size: 13px;
  cursor: pointer;
}

.back-login:hover {
  text-decoration: underline;
}

.back-login .arrow {
  margin-right: 2px;
  font-size: 17px;
  vertical-align: -1px;
  display: inline-block;
  transform: scaleX(0.7);
  transform-origin: center;
}

@media (max-width: 600px) {
  .forgot-password-page {
    padding: 20px 15px;
  }

  .forgot-password-card {
    padding: 15px 25px 22px;
  }

  .forgot-password-illustration {
    height: 145px;
  }

  .forgot-password-illustration img {
    width: 215px;
    height: 145px;
  }

  .forgot-password-card h1 {
    font-size: 24px;
  }

  .description {
    font-size: 13px;
  }
}
</style>
